import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import AlertsPanel from "@/components/AlertsPanel";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { OverallBadge } from "@/components/StatusBadge";
import WorkerAvatar from "@/components/WorkerAvatar";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { roleLabel } from "@/lib/checklist";
import InviteDialog from "@/components/InviteDialog";
import BulkImportDialog from "@/components/BulkImportDialog";
import {
  Users,
  ClipboardCheck,
  CheckCircle2,
  AlertOctagon,
  Timer,
  ArrowRight,
  Search,
  CalendarX,
  KeyRound,
  Sparkles,
  X,
  UserPlus,
  Send,
  MailCheck,
  Clock,
  AlertTriangle,
  Upload,
} from "lucide-react";

const OVERALL_ORDER = ["action_required", "in_progress", "outstanding", "compliant"];
const DEFAULT_BRAND_COLOR = "#012E88";
const NUDGE_DISMISS_KEY = "audentra.brandNudge.dismissed";

export default function ManagerDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [org, setOrg] = useState(null);
  const [locations, setLocations] = useState([]);
  const [inviteMetrics, setInviteMetrics] = useState(null);
  const [locationFilter, setLocationFilter] = useState("all");
  const [nudgeDismissed, setNudgeDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(NUDGE_DISMISS_KEY) === "1"
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteDefaultRole, setInviteDefaultRole] = useState("worker");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, w, a, o, l, m] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/workers"),
        api.get("/alerts"),
        api.get("/orgs/current").catch(() => ({ data: null })),
        api.get("/locations").catch(() => ({ data: [] })),
        api.get("/team/invite-metrics").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setWorkers(w.data);
      setAlerts(a.data);
      setOrg(o.data);
      setLocations(l.data || []);
      setInviteMetrics(m.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismissNudge = () => {
    localStorage.setItem(NUDGE_DISMISS_KEY, "1");
    setNudgeDismissed(true);
  };

  const brandIsDefault =
    org && (
      !org.brand_logo_url ||
      !org.brand_primary_color ||
      (org.brand_primary_color || "").toUpperCase() === DEFAULT_BRAND_COLOR.toUpperCase()
    );
  const showNudge = !!org && brandIsDefault && !nudgeDismissed;

  const filtered = workers
    .filter((row) => {
      if (locationFilter !== "all") {
        const wLoc = row.worker.location_id || "__none__";
        if (locationFilter === "unassigned" && row.worker.location_id) return false;
        if (locationFilter !== "unassigned" && wLoc !== locationFilter) return false;
      }
      const n = q.trim().toLowerCase();
      if (!n) return true;
      return (
        row.worker.name.toLowerCase().includes(n) ||
        row.worker.email.toLowerCase().includes(n) ||
        (row.worker.worker_role || "").toLowerCase().includes(n)
      );
    })
    .sort((a, b) => OVERALL_ORDER.indexOf(a.summary.overall) - OVERALL_ORDER.indexOf(b.summary.overall));

  const nurseCount =
    (stats?.role_breakdown || []).find((r) => r.key === "registered_nurse")?.count || 0;

  return (
    <AppLayout>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-caps text-slate-500">Dashboard</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">Compliance overview</h1>
          <p className="text-slate-600 mt-1.5">All workers, their role and their current compliance status.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setInviteDefaultRole("worker"); setInviteOpen(true); }}
            data-testid="dashboard-invite-worker"
            className="rounded-full h-10 px-4 border-teal-200 text-teal-800 hover:bg-teal-50"
          >
            <UserPlus className="h-4 w-4 mr-1.5" /> Invite worker
          </Button>
          {user?.role === "manager" && (
            <Button
              variant="outline"
              onClick={() => setBulkOpen(true)}
              data-testid="dashboard-bulk-import"
              className="rounded-full h-10 px-4"
            >
              <Upload className="h-4 w-4 mr-1.5" /> Bulk import
            </Button>
          )}
          <Button onClick={() => nav("/review")} data-testid="header-goto-review" className="bg-teal-700 hover:bg-teal-800 rounded-full h-10 px-5">
            Open review queue <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {showNudge && (
        <div
          className="mt-6 rounded-xl border border-teal-200 bg-gradient-to-r from-teal-50 to-white p-4 flex items-start gap-3"
          data-testid="brand-nudge-banner"
        >
          <div className="h-9 w-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base font-semibold text-slate-900">
              Make Audentra feel like <span className="text-teal-700">{org?.name || "your workspace"}</span>
            </div>
            <div className="text-sm text-slate-600 mt-0.5">
              Add your logo and brand colour so staff instantly recognise their workspace — it only takes a minute in Settings.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="bg-teal-700 hover:bg-teal-800 rounded-full h-9 px-4"
              onClick={() => nav("/settings")}
              data-testid="brand-nudge-cta"
            >
              Open Settings
            </Button>
            <button
              type="button"
              onClick={dismissNudge}
              className="h-9 w-9 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center"
              data-testid="brand-nudge-dismiss"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {alerts.length > 0 && (
        <div className="mt-6">
          <AlertsPanel alerts={alerts} showWorkerName />
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard testid="stat-total" icon={Users} label="Total workers" value={stats?.total_workers ?? "—"} />
        <StatCard testid="stat-compliant" icon={CheckCircle2} label="Fully compliant" value={stats?.compliant ?? "—"} accent="emerald" />
        <StatCard testid="stat-progress" icon={Timer} label="In progress" value={stats?.in_progress ?? "—"} accent="blue" />
        <StatCard testid="stat-action" icon={AlertOctagon} label="Action required" value={stats?.action_required ?? "—"} accent="rose" />
        <StatCard testid="stat-expired" icon={CalendarX} label="Expired docs" value={stats?.expired_docs ?? "—"} accent="rose" />
        <StatCard testid="stat-expiring" icon={Timer} label="Expiring ≤30d" value={stats?.expiring_soon_docs ?? "—"} accent="amber" />
        {nurseCount > 0 && (
          <StatCard testid="stat-nmc" icon={KeyRound} label="NMC checks due" value={stats?.nmc_checks_due ?? "—"} accent="blue" />
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="stat-pending" icon={ClipboardCheck} label="Pending review" value={stats?.pending_reviews ?? "—"} accent="amber" />
      </div>

      {inviteMetrics && (
        <InviteMetricsCard metrics={inviteMetrics} onOpenPending={() => nav("/team?tab=pending")} />
      )}

      {stats?.role_breakdown && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
          <div className="label-caps text-slate-500 mb-3">Workforce by role</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {stats.role_breakdown.map((r) => (
              <div key={r.key} data-testid={`role-count-${r.key}`} className="rounded-md border border-slate-200 px-3 py-2">
                <div className="text-xs text-slate-500">{r.label}</div>
                <div className="font-heading text-lg font-bold text-slate-900">{r.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
        <h2 className="font-heading text-xl font-semibold">Workers</h2>
        <div className="flex items-center gap-2 flex-1 justify-end flex-wrap">
          {locations.length > 0 && (
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger data-testid="workers-location-filter" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name} · {l.worker_count}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="relative w-full max-w-xs">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="workers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, role…" className="pl-9" />
          </div>
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-5 py-3 label-caps">Worker</th>
              <th className="text-left px-5 py-3 label-caps">Role</th>
              <th className="text-left px-5 py-3 label-caps">Progress</th>
              <th className="text-left px-5 py-3 label-caps">Flags</th>
              <th className="text-left px-5 py-3 label-caps">Status</th>
              <th className="text-right px-5 py-3 label-caps">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={6} className="px-5 py-8 text-slate-500">Loading workers…</td></tr>)}
            {!loading && filtered.length === 0 && (<tr><td colSpan={6} className="px-5 py-8 text-slate-500">No workers found.</td></tr>)}
            {!loading && filtered.map((row) => (
              <tr key={row.worker.id} data-testid={`worker-row-${row.worker.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <WorkerAvatar workerId={row.worker.id} name={row.worker.name} size="sm" />
                    <div>
                      <div className="font-semibold text-slate-900">{row.worker.name}</div>
                      <div className="text-xs text-slate-500">{row.worker.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-slate-700">{roleLabel(row.worker.worker_role)}</td>
                <td className="px-5 py-4 w-56">
                  <div className="flex items-center gap-2">
                    <Progress value={row.summary.progress || 0} className="h-1.5 flex-1" />
                    <span className="text-xs text-slate-600 tabular-nums">{row.summary.progress || 0}%</span>
                  </div>
                  <div className="text-[0.7rem] text-slate-500 mt-1">
                    {row.summary.approved}/{row.summary.total} in-date
                  </div>
                </td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap gap-1">
                    {(row.summary.expired || 0) > 0 && (
                      <span className="inline-flex rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold bg-rose-50 text-rose-700 border-rose-200" data-testid={`flag-expired-${row.worker.id}`}>
                        {row.summary.expired} expired
                      </span>
                    )}
                    {(row.summary.expiring_soon || 0) > 0 && (
                      <span className="inline-flex rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold bg-amber-50 text-amber-800 border-amber-200">
                        {row.summary.expiring_soon} expiring
                      </span>
                    )}
                    {(row.summary.rejected || 0) > 0 && (
                      <span className="inline-flex rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold bg-rose-50 text-rose-700 border-rose-200">
                        {row.summary.rejected} rejected
                      </span>
                    )}
                    {(row.summary.pending || 0) > 0 && (
                      <span className="inline-flex rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold bg-amber-50 text-amber-800 border-amber-200">
                        {row.summary.pending} in review
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-4"><OverallBadge overall={row.summary.overall} testid={`overall-${row.worker.id}`} /></td>
                <td className="px-5 py-4 text-right">
                  <Button variant="ghost" size="sm" data-testid={`worker-view-${row.worker.id}`} onClick={() => nav(`/workers/${row.worker.id}`)} className="text-teal-700 hover:text-teal-800 hover:bg-teal-50">
                    View <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        defaultRole={inviteDefaultRole}
        availableRoles={["worker", "team_member", "manager"]}
        onCreated={load}
      />
      <BulkImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={load}
      />
    </AppLayout>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  const accents = {
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-100",
    blue: "text-teal-700 bg-teal-50 border-teal-100",
    rose: "text-rose-600 bg-rose-50 border-rose-100",
    amber: "text-amber-600 bg-amber-50 border-amber-100",
  };
  const a = accents[accent] || "text-slate-600 bg-slate-100 border-slate-200";
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4 card-lift">
      <div className={`h-8 w-8 rounded-md border flex items-center justify-center ${a}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="font-heading text-2xl font-bold text-slate-900 mt-3 tabular-nums">{value}</div>
      <div className="text-[0.65rem] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}


function InviteMetricsCard({ metrics, onOpenPending }) {
  const empty =
    (metrics.sent_this_week || 0) === 0 &&
    (metrics.accepted_this_week || 0) === 0 &&
    (metrics.pending_total || 0) === 0;
  const items = [
    { key: "sent", label: "Sent this week", value: metrics.sent_this_week ?? 0, icon: Send, tone: "text-teal-700 bg-teal-50 border-teal-100" },
    { key: "accepted", label: "Accepted this week", value: metrics.accepted_this_week ?? 0, icon: MailCheck, tone: "text-emerald-600 bg-emerald-50 border-emerald-100" },
    { key: "pending", label: "Still pending", value: metrics.pending_total ?? 0, icon: Clock, tone: "text-amber-600 bg-amber-50 border-amber-100" },
    { key: "expiring", label: "Expiring <24h", value: metrics.expiring_soon ?? 0, icon: AlertTriangle, tone: "text-rose-600 bg-rose-50 border-rose-100" },
  ];
  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="invite-metrics-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-caps text-slate-500">Invites · last 7 days</div>
          <div className="font-heading text-lg font-semibold text-slate-900 mt-0.5">
            {metrics.acceptance_rate !== null && metrics.acceptance_rate !== undefined
              ? `${metrics.acceptance_rate}% of this week's invites accepted`
              : "Onboarding pipeline"}
          </div>
        </div>
        {(metrics.pending_total || 0) > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenPending}
            data-testid="invite-metrics-open-pending"
            className="rounded-full h-9 px-4 border-teal-200 text-teal-800 hover:bg-teal-50"
          >
            Review {metrics.pending_total} pending
          </Button>
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.key} data-testid={`invite-metric-${it.key}`} className="rounded-md border border-slate-200 px-3 py-3">
            <div className={`h-7 w-7 rounded border flex items-center justify-center ${it.tone}`}>
              <it.icon className="h-3.5 w-3.5" />
            </div>
            <div className="font-heading text-xl font-bold text-slate-900 mt-2 tabular-nums">{it.value}</div>
            <div className="text-[0.65rem] uppercase tracking-widest text-slate-500 mt-0.5">{it.label}</div>
          </div>
        ))}
      </div>
      {empty && (
        <div className="mt-3 text-xs text-slate-500">
          No invite activity yet — invite a worker to see this update in real time.
        </div>
      )}
    </section>
  );
}
